import { useEffect, useMemo, useRef, useState, type FunctionComponent } from "react";
import {
  CalendarOutlined,
  CheckSquareOutlined,
  FileDoneOutlined,
  InfoCircleOutlined,
  MessageOutlined,
  TagOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Avatar,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  List,
  message,
  Popconfirm,
  Select,
  Space,
  Tree,
  Typography,
} from "antd";
import { type FormInstance } from "antd/lib/form";
import dayjs, { type Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import sortBy from "lodash.sortby";
import isEqual from "lodash.isequal";
import orderBy from "lodash.orderby";
import uniq from "lodash.uniq";
import { BadgeAlertIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { programAutomationActions, traineeProgramActions } from "@fitness-app/app-store";
import { TaskType } from "@fitness-app/data-models/entities/Automation";
import {
  TemplateType,
  type BaseSurveyTemplate,
  type ScheduledSurveyAutomation,
} from "@fitness-app/data-models/entities/AutomationTemplate";
import { SurveyType } from "@fitness-app/data-models/entities/ClientSurvey";
import {
  type DayContent,
  type ProgramAutomation,
  type ScheduledItems,
  type WorkoutRoutine,
} from "@fitness-app/data-models/entities/ProgramAutomation";
import { getUserInitials } from "@fitness-app/utils";
import { createAutomationSchedule } from "@fitness-app/utils/src/programAutomation/createAutomationSchedule";

import { useAuthorOptions } from "~/hooks/trainer/useAuthorOptions";
import ScheduleProgramStepper, {
  useScheduleProgramStepper,
} from "~/modules/Automation/ScheduleProgramStepper/ScheduleProgramStepper";
import { formatSentTimeLong } from "~/modules/Automation/utils/formatSentTime";
import AutomationVariablesForm from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeCalendar/components/AutomationScheduleSelectorForm/AutomationVariablesForm";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

dayjs.extend(isoWeek);
dayjs.extend(isSameOrBefore);

dayjs.locale("pl");

type AutomationScheduleSelectorFormModel = {
  automationTemplate: string;
  firstDay: Date;
  packageEndDate?: Date;
  authorId?: string;
  /** Per-assignment override of the automation's channel routing (TRE-801). */
  channelRouting?: "automation" | "default" | "marketing";
};

interface OwnProps {
  formController?: FormInstance<AutomationScheduleSelectorFormModel>;
  onSubmit: ({
    events,
    scheduledPrograms,
    startDate,
    clientId,
    programAutomationId,
    programAutomationName,
    packageRange,
  }: {
    events: ScheduledItems[];
    scheduledPrograms: WorkoutRoutine[];
    startDate: string;
    clientId: string;
    programAutomationId: string;
    programAutomationName: string;
    packageRange?: { startDate: string; endDate: string } | null;
  }) => void;
}

type Props = OwnProps;

const today = dayjs();

const SEPARATOR = "_";

const AutomationScheduleSelectorForm: FunctionComponent<Props> = ({ onSubmit, formController }) => {
  const { t } = useTranslation(["automation", "common"]);
  const { data } = useAppSelector((store) => store.programAutomation);
  const [variableFormController] = Form.useForm<Record<string, string | number>>();

  const clientData = useAppSelector((store) => store.trainee.profile);
  const { selectedProgram: activeProgram, scheduledPrograms } = useAppSelector((store) => store.traineeProgram);
  const {
    visible: showScheduledProgramEditor,
    closeModal: closeEditModal,
    openModal: openEditModal,
    routineModel,
  } = useScheduleProgramStepper();
  const [selectedAutomation, setAutomation] = useState<null | ProgramAutomation | undefined>();
  const [startDate, setStartDate] = useState<null | Dayjs>(null);
  const [packageEndDate, setPackageEndDate] = useState<null | Dayjs>(null);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const previousKeys = useRef<string[]>([]);
  const dispatch = useAppDispatch();
  const surveyTemplates = useAppSelector((store) => store.surveysTemplates.list);
  const surveyTemplatesById = useMemo(() => {
    return Object.fromEntries(
      surveyTemplates.map((item) => {
        if (!item.automation) {
          return [item.id, {} as Record<string, string>];
        }

        return [
          item.id,
          Object.fromEntries(
            uniq(
              Object.values(item.automation)
                .map((automations) =>
                  automations.map((automation) => automation.tasks.map((task) => task.variables)).flat(2),
                )
                .flat(2),
            )
              .filter((variable): variable is string => typeof variable === "string")
              .sort()
              .map((variable) => [variable, ""]),
          ) as Record<string, string>,
        ];
      }),
    );
  }, [surveyTemplates]);

  useEffect(() => {
    void dispatch(programAutomationActions.fetchProgramAutomations());
  }, []);

  const traineeId = clientData?.id;

  useEffect(() => {
    if (traineeId) {
      void dispatch(traineeProgramActions.fetchScheduledPrograms({ traineeId }));
    }
  }, [dispatch, traineeId]);

  const schedule = useMemo(
    () =>
      createAutomationSchedule(
        selectedAutomation,
        startDate,
        startDate && packageEndDate
          ? {
              startDate: startDate.format("YYYY-MM-DD"),
              endDate: packageEndDate.format("YYYY-MM-DD"),
            }
          : null,
      ),
    [selectedAutomation, startDate, packageEndDate],
  );

  useEffect(() => {
    const allKeys = schedule
      .map((item) => item.day.items.map((dayItem) => `${item.day.id}${SEPARATOR}${dayItem.id}`))
      .flat();
    if (startDate && !isEqual(previousKeys.current, allKeys)) {
      setSelectedKeys(allKeys);
    }
    previousKeys.current = allKeys;
  }, [selectedAutomation, startDate]);

  const onAutomationSelect = async (value: string) => {
    const automation = await dispatch(programAutomationActions.fetchProgramAutomation(value)).unwrap();
    setAutomation(automation);
  };

  const authorOptions = useAuthorOptions();

  /** Every task key in the schedule, in tree order — the unit the checkboxes operate on. */
  const allTaskKeys = useMemo(
    () => schedule.flatMap((entry) => entry.day.items.map((dayItem) => `${entry.day.id}${SEPARATOR}${dayItem.id}`)),
    [schedule],
  );

  /**
   * One group per calendar day, not per task.
   *
   * `createAutomationSchedule` emits a separate entry for each item of a configured day, because a
   * package-anchored item can resolve to a different date than the day it sits on — but every entry
   * keeps that day's `id`. Rendered one-to-one, a day holding ten tasks became ten sibling nodes,
   * all titled with the same date and all sharing a single antd key. The duplicated key is why they
   * could not be unticked: antd applies a toggle to every node under that key at once.
   *
   * Dates are ordered by parsing the `DD-MM-YYYY` label by hand rather than through dayjs, which
   * needs the customParseFormat plugin to read a custom format and silently returns Invalid Date
   * without it.
   */
  const scheduleByDate = useMemo(() => {
    const groups = new Map<string, { dayId: string; dayItem: DayContent["items"][number] }[]>();

    for (const entry of schedule) {
      const bucket = groups.get(entry.dayDate) ?? [];
      for (const dayItem of entry.day.items) {
        bucket.push({ dayId: entry.day.id, dayItem });
      }
      groups.set(entry.dayDate, bucket);
    }

    const sortValue = (dayDate: string) => {
      const [day, month, year] = dayDate.split("-").map(Number);
      return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1).getTime();
    };

    return [...groups.entries()]
      .map(([dayDate, items]) => ({ dayDate, items }))
      .sort((a, b) => sortValue(a.dayDate) - sortValue(b.dayDate));
  }, [schedule]);

  const treeData = useMemo(
    () =>
      scheduleByDate.map((group) => ({
        title: group.dayDate,
        // No underscore, so the submit path's `key.split(SEPARATOR)[1]` never mistakes a day node
        // for a task id.
        key: `day:${group.dayDate}`,
        icon: <CalendarOutlined />,
        children: orderBy(group.items, ["dayItem.sentTime.hours", "dayItem.sentTime.minutes"]).map(
          ({ dayId, dayItem }) => ({
            title: `${formatSentTimeLong(dayItem.sentTime)} - ${
              "name" in dayItem
                ? dayItem.name
                : dayItem.type === TaskType.FitnessApp
                  ? `${t(`common:fitnessAppTask.${dayItem.action.type}`)} - ${dayItem.action.tags.join(", ")}`
                  : dayItem.type === TaskType.TeamMemberTask
                    ? `${dayItem.title}`
                    : `${t(`common:emailClientTask.${dayItem.action.type}`)} - ${
                        "emailLists" in dayItem.action
                          ? dayItem.action.emailLists.map((item: { client: string }) => `${item.client}`).join(", ")
                          : dayItem.action.emailClients
                              .map((item: { client: string; account: string }) => `${item.client} - ${item.account}`)
                              .join(", ")
                      }`
            }`,
            icon:
              dayItem.type === TemplateType.ScheduledMessages ? (
                <MessageOutlined />
              ) : dayItem.type === TemplateType.ScheduledSurvey ? (
                <FileDoneOutlined />
              ) : dayItem.type === TemplateType.ScheduledPopUpFromAutomation ? (
                <BadgeAlertIcon size={16} className="align-text-top" />
              ) : dayItem.type === TaskType.TeamMemberTask ? (
                <CheckSquareOutlined />
              ) : (
                <TagOutlined />
              ),
            key: `${dayId}${SEPARATOR}${dayItem.id}`,
            children:
              dayItem.type === TemplateType.ScheduledMessages
                ? dayItem.messages.map((message, i) => ({
                    title: `- ${message}`,
                    key: `${dayId}-${dayItem.id}-${i}`,
                    checkable: false,
                  }))
                : [],
          }),
        ),
      })),
    [scheduleByDate, t],
  );

  // antd adds day-node keys to `checkedKeys` once all of a day's tasks are ticked, so the counter
  // and the switch state must look at task keys only.
  const selectedTaskKeys = useMemo(
    () => selectedKeys.filter((key) => allTaskKeys.includes(key)),
    [selectedKeys, allTaskKeys],
  );
  const allTasksChecked = allTaskKeys.length > 0 && selectedTaskKeys.length === allTaskKeys.length;
  const someTasksChecked = selectedTaskKeys.length > 0 && !allTasksChecked;

  const routinesWithDates = useMemo(() => {
    if (!selectedAutomation?.scheduledWorkoutsRoutine?.length || !startDate) {
      return [];
    }
    let previousEndDate: null | Dayjs = null;

    return sortBy(selectedAutomation.scheduledWorkoutsRoutine, "startWeek").map((routine, i) => {
      if (!selectedAutomation.scheduledWorkoutsRoutine[i - 1]) {
        let moveToNextWeek = false;
        previousEndDate = startDate.add(routine.duration, "week").subtract(1, "day").endOf("day");
        if (routine.startWeek === 1 && routine.requireFullFirstWeek && startDate.isoWeekday() !== 1) {
          previousEndDate = previousEndDate.endOf("isoWeek");
          moveToNextWeek = true;
        }
        return {
          ...routine,
          startAt: startDate,
          endAt: previousEndDate,
          moveToNextWeek,
        };
      }

      if (!previousEndDate) {
        throw new Error("Cannot calculate start date for scheduled program");
      }

      const nextStartDate = previousEndDate.add(1, "day");
      previousEndDate = nextStartDate.add(routine.duration, "week").subtract(1, "day").endOf("day");

      return {
        ...routine,
        startAt: nextStartDate,
        endAt: previousEndDate,
      };
    });
  }, [selectedAutomation, startDate]);

  const { hasRecurringSurvey, variables } = useMemo(() => {
    const surveys = schedule
      .map((item) =>
        item.day.items.some(
          (item) => item.type === TemplateType.ScheduledSurvey && item.survey.type === SurveyType.Recurring,
        ),
      )
      .flat()
      .some(Boolean);

    const variables = schedule
      .map((item) =>
        item.day.items.filter((item): item is ScheduledSurveyAutomation => {
          return Boolean(
            item.type === TemplateType.ScheduledSurvey &&
            (item.survey.variables ||
              (item.survey.templateId && Object.keys(surveyTemplatesById[item.survey.templateId]!)?.length)),
          );
        }),
      )
      .flat()
      .map((item) =>
        item.survey.templateId
          ? {
              ...surveyTemplatesById[item.survey.templateId],
              ...item.survey.variables,
            }
          : item.survey.variables,
      )
      .reduce<BaseSurveyTemplate["variables"]>((acc, current) => {
        return {
          ...acc,
          ...current,
        };
      }, {});

    return {
      hasRecurringSurvey: surveys,
      variables,
    };
  }, [schedule, surveyTemplatesById]);

  const handleSubmit = async (formData: AutomationScheduleSelectorFormModel) => {
    if (!startDate || !selectedAutomation || !clientData) {
      return null;
    }

    try {
      const values = await variableFormController?.validateFields();

      const scheduledEventIds = selectedKeys.map((key) => key.split(SEPARATOR)[1]);

      const foundAuthorData = authorOptions.find((author) => author.value === formData.authorId);

      // "automation" (default) sends no flag — the backend stamps it from the
      // automation's chatConfiguration; an explicit choice here overrides it per assignment.
      const channelOverride =
        formData.channelRouting === "marketing"
          ? { sendToDefaultChannel: false }
          : formData.channelRouting === "default"
            ? { sendToDefaultChannel: true }
            : {};

      const selectedEvents = schedule
        .map((item) => ({
          sendDate: dayjs(item.dayDate, "DD-MM-YYYY").format("YYYY-MM-DD"),
          id: item.day.id,
          items: item.day.items
            .filter((event) => scheduledEventIds.includes(event.id))
            .map((task) =>
              task.type === TemplateType.ScheduledMessages
                ? {
                    ...task,
                    ...(foundAuthorData
                      ? {
                          authorId: foundAuthorData.value,
                          authorRole: foundAuthorData.role,
                          authorName: foundAuthorData.label,
                          authorAvatar: foundAuthorData.avatarUrl ?? null,
                        }
                      : {}),
                    ...channelOverride,
                  }
                : task.type === TemplateType.ScheduledSurvey
                  ? {
                      ...task,
                      survey: {
                        ...task.survey,
                        variables: {
                          ...(task.survey.variables || {}),
                          ...values,
                        },
                      },
                    }
                  : task,
            ),
        }))
        .filter((day) => day.items.length);

      onSubmit({
        events: selectedEvents,
        startDate: startDate.format("YYYY-MM-DD"),
        scheduledPrograms: routinesWithDates.map((routine) => {
          if ("moveToNextWeek" in routine) {
            const { moveToNextWeek, ...rest } = routine;
            return {
              ...rest,
            };
          }
          return routine;
        }),
        clientId: clientData.id,
        programAutomationId: selectedAutomation.id,
        programAutomationName: selectedAutomation.name,
        packageRange: packageEndDate
          ? {
              startDate: startDate.format("YYYY-MM-DD"),
              endDate: packageEndDate.format("YYYY-MM-DD"),
            }
          : null,
      });
    } catch {
      void message.error("Uzupełnij wymagane pola formularza");
    }
  };

  const showAlertAboutProgramOverride =
    activeProgram && routinesWithDates[0]?.startAt.isSameOrBefore(dayjs(activeProgram.endDate, "YYYY-MM-DD"), "date");

  const onSubmitScheduledProgram = (workoutRoutine: WorkoutRoutine) => {
    if (selectedAutomation?.scheduledWorkoutsRoutine?.length) {
      setAutomation({
        ...selectedAutomation,
        scheduledWorkoutsRoutine: selectedAutomation.scheduledWorkoutsRoutine.map((item) =>
          item.id === workoutRoutine.id ? workoutRoutine : item,
        ),
      });
    }

    closeEditModal();
  };

  const onSubmitFormVariables = (_model: Record<string, string | number>) => {};

  return (
    <>
      <Form<AutomationScheduleSelectorFormModel>
        name="login-form"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        layout="horizontal"
        form={formController}
        onFinish={handleSubmit}
      >
        <Form.Item
          name="automationTemplate"
          label={t("automationSchedule.selectSchedule")}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Select
            showSearch
            onSelect={onAutomationSelect}
            placeholder={t("automationSchedule.selectSchedulePlaceholder")}
            filterOption={(inputValue, option) => {
              if (option && "label" in option) {
                return String(option?.label).toUpperCase().indexOf(inputValue?.toUpperCase()) !== -1;
              }

              return false;
            }}
            options={data.map((automation) => ({
              value: automation.id,
              label: automation.name,
            }))}
          />
        </Form.Item>
        <Form.Item
          name="firstDay"
          label={t("automationSchedule.firstDay")}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <DatePicker
            format="DD/MM/YYYY"
            onChange={(date) => {
              setStartDate(date);
              // a start date moved past the selected package end makes the range inverted - clear it
              if (date && packageEndDate && packageEndDate.isBefore(date, "day")) {
                setPackageEndDate(null);
                formController?.setFieldValue("packageEndDate", null);
              }
            }}
            disabledDate={(current) => dayjs(current).isBefore(today.startOf("week"), "day")}
          />
        </Form.Item>

        <Form.Item
          name="packageEndDate"
          label="Koniec pakietu"
          dependencies={["firstDay"]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const firstDay = getFieldValue("firstDay") as Dayjs | undefined;
                if (!value || !firstDay || !dayjs(value).isBefore(dayjs(firstDay), "day")) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Koniec pakietu nie może być przed datą rozpoczęcia"));
              },
            }),
          ]}
        >
          <DatePicker
            format="DD/MM/YYYY"
            onChange={setPackageEndDate}
            disabledDate={(current) => Boolean(startDate && dayjs(current).isBefore(startDate, "day"))}
          />
        </Form.Item>

        {treeData.length ? (
          <Form.Item
            name="authorId"
            label={t("automatedMessageForm.messageAuthor")}
            rules={[
              {
                required: true,
                message: t<string>("common:validationErrors.fieldIsRequired"),
              },
            ]}
          >
            <Select placeholder={t("automatedMessageForm.messageAuthorPlaceholder")}>
              {authorOptions.map((author) => (
                <Select.Option value={author.value} label={author.label} key={author.value}>
                  <Space direction="horizontal">
                    <Avatar src={author.avatarUrl} size={26}>
                      {getUserInitials(author.label)}
                    </Avatar>{" "}
                    {author.label}
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        ) : null}

        {treeData.length ? (
          <Form.Item
            name="channelRouting"
            label="Kanał wiadomości"
            initialValue="automation"
            tooltip="Nadpisuje ustawienie z automatyzacji dla tego przypisania. Kanał ogłoszeniowy wymaga, żeby klientka go miała — inaczej wiadomości trafią na zwykły czat."
          >
            <Select
              options={[
                { value: "automation", label: "Jak w ustawieniach automatyzacji" },
                { value: "default", label: "Zwykły czat" },
                { value: "marketing", label: "Kanał ogłoszeniowy" },
              ]}
            />
          </Form.Item>
        ) : null}
      </Form>
      {routinesWithDates.length ? (
        <div>
          <Typography.Title level={5}>Zaplanowane programy w automatyzacji:</Typography.Title>
          {showAlertAboutProgramOverride && (
            <Alert
              message={`Wybrana automatyzacja nadpiszę obecnie dodany plan treningowy, który kończy się ${dayjs(
                activeProgram?.endDate,
                "YYYY-MM-DD",
              ).format("DD.MM.YYYY")}`}
              showIcon
              type="warning"
              style={{
                marginBottom: 12,
              }}
            />
          )}

          <List
            itemLayout="horizontal"
            bordered
            dataSource={routinesWithDates}
            renderItem={(routine) => (
              <List.Item
                actions={[
                  <Typography.Link key="edit" onClick={() => openEditModal(routine)}>
                    edytuj
                  </Typography.Link>,
                  <Popconfirm
                    key="remove"
                    title="Czy na pewno chcesz usunąc ten program z automatyzacji?"
                    okText="Tak"
                    cancelText="Nie"
                    placement="left"
                    onConfirm={() =>
                      setAutomation((prev) => {
                        if (!prev?.scheduledWorkoutsRoutine) {
                          return prev;
                        }
                        return {
                          ...prev,
                          scheduledWorkoutsRoutine: prev.scheduledWorkoutsRoutine.filter(
                            (item) => item.id !== routine.id,
                          ),
                        };
                      })
                    }
                  >
                    <Typography.Link type="danger">usuń</Typography.Link>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  title={<Typography.Text strong>{routine.name}</Typography.Text>}
                  description={
                    <div className="space-y-2">
                      <Space direction="horizontal" size={8}>
                        <CalendarOutlined />
                        <Typography.Text type="secondary">
                          {routine.startWeek !== routine.endWeek
                            ? `Plan od ${routine.startAt.format("DD.MM.YYYY")} do ${routine.endAt.format("DD.MM.YYYY")}`
                            : `Plan dla tygodnia ${routine.startWeek}`}
                        </Typography.Text>
                      </Space>
                      {"moveToNextWeek" in routine && routine.moveToNextWeek && (
                        <div>
                          <Typography.Text underline>
                            <InfoCircleOutlined />
                            <span className="ml-1">
                              Plan został przesunięty o dodatkowe dni ponieważ wymaga zakończenia w niedzielę
                            </span>
                          </Typography.Text>
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
          {scheduledPrograms?.length ? (
            <Typography.Text type="secondary">
              Zaplanowane obecnie programy treningowe w przyszłości zostaną nadpisane przez te zmiany
            </Typography.Text>
          ) : null}
        </div>
      ) : null}

      <div>
        <Divider orientation="center">Harmonogram zadań</Divider>
        {allTaskKeys.length ? (
          <Checkbox
            className="mb-2"
            checked={allTasksChecked}
            indeterminate={someTasksChecked}
            onChange={(event) => setSelectedKeys(event.target.checked ? allTaskKeys : [])}
          >
            {allTasksChecked ? "Odznacz wszystkie" : "Zaznacz wszystkie"} ({selectedTaskKeys.length}/
            {allTaskKeys.length})
          </Checkbox>
        ) : null}
        <Tree
          checkable
          showIcon
          selectable={false}
          checkedKeys={selectedKeys}
          onCheck={(keys) => setSelectedKeys(keys as string[])}
          treeData={treeData}
        />
      </div>
      {selectedAutomation && (
        <ScheduleProgramStepper
          onSubmit={onSubmitScheduledProgram}
          visible={showScheduledProgramEditor}
          handleClose={closeEditModal}
          routineModel={routineModel || undefined}
          selectedAutomation={selectedAutomation}
          withoutWeeksLimit
        />
      )}

      <div className="mt-4">
        {hasRecurringSurvey && (
          <Typography.Text type="secondary" className="text-sm">
            W harmonogramie zadań widzisz tylko pierwsze wystąpienie cyklicznych ankiet. Kolejne wystąpienia będą
            generowanie automatycznie po wysłaniu poprzednich ankiet.
          </Typography.Text>
        )}
      </div>

      {variables && Object.keys(variables).length ? (
        <AutomationVariablesForm
          formController={variableFormController}
          model={variables}
          variables={Object.keys(variables)}
          onSubmit={onSubmitFormVariables}
        />
      ) : null}
    </>
  );
};

export default AutomationScheduleSelectorForm;
